/**
 * PFAP Hub — Guide Mode (Phase 1) overlay component.
 *
 * Renders a semi-transparent dark overlay with a centered card that
 * explains the current page (title, paragraph, "What this page is
 * used for" bullets, "What you should pay attention to" bullets, and
 * an optional role-specific note).
 *
 * The page behind the overlay STAYS rendered and visible — the
 * overlay never blocks app loading, never traps focus permanently, and
 * is always dismissible with three buttons:
 *
 *   - Got it                      → close for current session/page view
 *   - Don't show this page again  → permanently dismiss for this user
 *   - Turn off Guide Mode         → set guide_mode_enabled=false
 *
 * The overlay is auto-mounted by the Layout via `<RouteGuide />`. It
 * resolves the current pathname → guide_id and only renders if:
 *   - Guide Mode is ON
 *   - the guide hasn't been dismissed (per-user OR per-session)
 *   - the user's role has non-hidden content for that guide
 */
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useGuideMode } from '../contexts/GuideModeContext';
import { GUIDE_BY_ID, guideIdForPath } from '../data/guideContentRegistry';
import { Button } from './ui/button';
import { X, BookOpen, Lightbulb, AlertCircle } from 'lucide-react';

const ROLE_FALLBACK = ['Admin', 'Staff', 'Player', 'Parent'];

function pickRoleContent(entry, role) {
  if (!entry || !entry.roles) return null;
  // Try exact role first.
  let block = entry.roles[role];
  // Some apps may store role in lower-case — try title-cased fallback.
  if (!block && typeof role === 'string') {
    const tc = role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
    block = entry.roles[tc];
  }
  if (!block) return null;
  if (block.hidden) return null;
  return block;
}

const GuideOverlay = ({ guideId, onClose }) => {
  const { user } = useAuth();
  const { dismissGuide, dismissForSession, setEnabled } = useGuideMode();
  const [closing, setClosing] = useState(false);

  const entry = GUIDE_BY_ID[guideId];
  const role = user?.role || ROLE_FALLBACK[0];
  const block = pickRoleContent(entry, role);

  // Esc closes for the session.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') handleGotIt();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!entry || !block) return null;

  const handleGotIt = () => {
    if (closing) return;
    setClosing(true);
    dismissForSession(guideId);
    onClose && onClose();
  };

  const handleDontShow = async () => {
    if (closing) return;
    setClosing(true);
    await dismissGuide(guideId, entry.version || 1);
    onClose && onClose();
  };

  const handleTurnOff = async () => {
    if (closing) return;
    setClosing(true);
    await setEnabled(false);
    onClose && onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-3 py-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby={`guide-overlay-title-${guideId}`}
      data-testid={`guide-overlay-${guideId}`}
    >
      {/* Dim, click-through-to-close backdrop. */}
      <button
        type="button"
        aria-label="Close guide"
        onClick={handleGotIt}
        className="absolute inset-0 bg-black/60 dark:bg-black/70 backdrop-blur-[2px]"
        data-testid="guide-overlay-backdrop"
      />

      <div
        className="relative w-full max-w-xl bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden"
        data-testid="guide-overlay-card"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-3 border-b border-gray-100 dark:border-gray-800">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 flex items-center justify-center shrink-0">
              <BookOpen size={18} />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-wide text-emerald-700 dark:text-emerald-400 font-medium">
                Guide
              </div>
              <h2
                id={`guide-overlay-title-${guideId}`}
                className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 leading-tight"
                data-testid="guide-overlay-title"
              >
                {entry.title}
              </h2>
            </div>
          </div>
          <button
            type="button"
            onClick={handleGotIt}
            className="p-1.5 rounded-md text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            aria-label="Close"
            data-testid="guide-overlay-close-x"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-3 max-h-[65vh] overflow-y-auto" data-testid="guide-overlay-body">
          <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed" data-testid="guide-overlay-explanation">
            {block.explanation}
          </p>

          {Array.isArray(block.used_for) && block.used_for.length > 0 && (
            <section data-testid="guide-overlay-used-for">
              <div className="flex items-center gap-1.5 text-xs font-medium text-gray-900 dark:text-gray-100 mb-1.5">
                <Lightbulb size={12} className="text-emerald-600" />
                What this page is used for
              </div>
              <ul className="list-disc list-outside pl-5 space-y-0.5 text-[12.5px] text-gray-700 dark:text-gray-300 leading-relaxed">
                {block.used_for.map((line, i) => (
                  <li key={i}>{line}</li>
                ))}
              </ul>
            </section>
          )}

          {Array.isArray(block.attention) && block.attention.length > 0 && (
            <section data-testid="guide-overlay-attention">
              <div className="flex items-center gap-1.5 text-xs font-medium text-gray-900 dark:text-gray-100 mb-1.5">
                <AlertCircle size={12} className="text-amber-600" />
                What you should pay attention to
              </div>
              <ul className="list-disc list-outside pl-5 space-y-0.5 text-[12.5px] text-gray-700 dark:text-gray-300 leading-relaxed">
                {block.attention.map((line, i) => (
                  <li key={i}>{line}</li>
                ))}
              </ul>
            </section>
          )}

          {block.role_note && (
            <div
              data-testid="guide-overlay-role-note"
              className="px-3 py-2 rounded-md border border-emerald-200 dark:border-emerald-900 bg-emerald-50/70 dark:bg-emerald-900/15 text-[12px] text-emerald-900 dark:text-emerald-200"
            >
              <span className="font-medium">Note:</span> {block.role_note}
            </div>
          )}

          <p className="text-[10px] text-gray-400 dark:text-gray-500 italic pt-1">
            You can turn Guide Mode on or off anytime from the sidebar.
          </p>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-2 px-5 py-3 bg-gray-50 dark:bg-gray-900/40 border-t border-gray-100 dark:border-gray-800" data-testid="guide-overlay-actions">
          <Button
            onClick={handleGotIt}
            className="flex-1 bg-pfap-green hover:bg-pfap-green/90 text-white"
            data-testid="guide-got-it-btn"
            disabled={closing}
          >
            Got it
          </Button>
          <Button
            variant="outline"
            onClick={handleDontShow}
            className="flex-1 text-xs sm:text-sm"
            data-testid="guide-dont-show-btn"
            disabled={closing}
          >
            Don't show this page again
          </Button>
          <Button
            variant="ghost"
            onClick={handleTurnOff}
            className="flex-1 text-xs sm:text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
            data-testid="guide-turn-off-btn"
            disabled={closing}
          >
            Turn off Guide Mode
          </Button>
        </div>
      </div>
    </div>
  );
};

/**
 * Auto-mounted by Layout. Resolves the current path → guide_id and
 * renders the overlay when appropriate. No-op when:
 *   - Guide Mode is OFF, or
 *   - the path has no registered guide, or
 *   - the user's role has hidden content for that guide, or
 *   - the guide has been dismissed (persistent OR session) AT THE
 *     CURRENT VERSION. A bump of `entry.version` re-shows the guide
 *     once for users whose stored `guide_versions_seen[gid]` is older.
 */
export const RouteGuide = () => {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const {
    guide_mode_enabled,
    isDismissed,
    loading,
    guide_versions_seen,
    sessionDismissed,
  } = useGuideMode();
  const [forceClose, setForceClose] = useState(false);
  const [lastPath, setLastPath] = useState(pathname);

  // Reset the per-page session-close flag whenever we navigate to a
  // genuinely different guide path so a "Got it" on /training doesn't
  // suppress the /matches guide on the same browser session.
  useEffect(() => {
    if (pathname !== lastPath) {
      setForceClose(false);
      setLastPath(pathname);
    }
  }, [pathname, lastPath]);

  if (loading || !user || !guide_mode_enabled) return null;

  const gid = guideIdForPath(pathname);
  if (!gid) return null;
  if (forceClose) return null;

  const entry = GUIDE_BY_ID[gid];
  if (!entry) return null;

  // Version-aware dismissal: if the registry's version is newer than
  // the version the user previously dismissed (or no version stored),
  // we IGNORE the persistent dismissal and re-show once. Per-session
  // dismissal still wins so the overlay does not bounce back on the
  // same page view after a "Got it" click. (V2.6 — needed so the
  // Focus Hub V2 refresh is visible to users who dismissed v1.)
  const seenVersion = (guide_versions_seen && guide_versions_seen[gid]) || 0;
  const isStaleDismissal = seenVersion < (entry.version || 1);
  const sessionClosed = sessionDismissed && sessionDismissed.has(gid);
  if (sessionClosed) return null;
  if (!isStaleDismissal && isDismissed(gid)) return null;

  // Only render if there's role-eligible content. This is also enforced
  // inside <GuideOverlay /> but doing it here avoids mounting the dialog
  // when the role would just see nothing.
  const role = user.role;
  const block = pickRoleContent(entry, role);
  if (!block) return null;

  return (
    <GuideOverlay
      key={`${gid}-v${entry.version || 1}`}
      guideId={gid}
      onClose={() => setForceClose(true)}
    />
  );
};

export default GuideOverlay;
